import { Building2, Calculator, Gem, Landmark } from "lucide-react";
import { Link } from "@tanstack/react-router";

/**
 * HubNextSteps — reusable "next-step" CTA block.
 *
 * Four variants: gold-pk, gold-in, silver-pk, silver-in.
 * Each CTA row IS the sentence: native-script label + Click here pill.
 * No intro paragraph — direct button-first design per product spec.
 * Urdu (RTL) for PK, Hindi (LTR) for IN.
 */
type Variant = "gold-pk" | "gold-in" | "silver-pk" | "silver-in";
type Props = { variant: Variant };

type Cta = { href: string; icon: "city" | "silver" | "gold" | "zakat" | "calculator"; label: string };
type Config = {
  dir: "rtl" | "ltr";
  lang: string;
  ctas: Cta[];
};

const CONFIG: Record<Variant, Config> = {
  "gold-pk": {
    dir: "rtl",
    lang: "ur",
    ctas: [
      { href: "/pakistan/cities", icon: "city", label: "اپنے شہر میں سونے کی قیمت دیکھنے کے لیے یہاں کلک کریں" },
      { href: "/silver-rate-pakistan", icon: "silver", label: "چاندی (سلور) کی قیمت دیکھنے کے لیے یہاں کلک کریں" },
      { href: "/pakistan/zakat-nisab", icon: "zakat", label: "اپنے سونے پر کتنی زکوٰۃ بنتی ہے، ابھی چیک کریں" },
    ],
  },
  "gold-in": {
    dir: "ltr",
    lang: "hi",
    ctas: [
      { href: "/india/cities", icon: "city", label: "अपने शहर में सोने की कीमत देखने के लिए यहाँ क्लिक करें" },
      { href: "/silver-rate-india", icon: "silver", label: "चांदी की कीमत देखने के लिए यहाँ क्लिक करें" },
      { href: "/zakat-calculator", icon: "zakat", label: "अपने सोने पर कितनी ज़कात बनती है, अभी चेक करें" },
    ],
  },
  "silver-pk": {
    dir: "rtl",
    lang: "ur",
    ctas: [
      { href: "/pakistan/silver-cities", icon: "city", label: "اپنے شہر میں چاندی کی قیمت دیکھنے کے لیے یہاں کلک کریں" },
      { href: "/pakistan", icon: "gold", label: "سونے کی قیمت دیکھنے کے لیے یہاں کلک کریں" },
      { href: "/pakistan/zakat-nisab", icon: "zakat", label: "اپنے سونے پر کتنی زکوٰۃ بنتی ہے، ابھی چیک کریں" },
    ],
  },
  "silver-in": {
    dir: "ltr",
    lang: "hi",
    ctas: [
      { href: "/india/silver-cities", icon: "city", label: "अपने शहर में चांदी की कीमत देखने के लिए यहाँ क्लिक करें" },
      { href: "/india", icon: "gold", label: "सोने की कीमत देखने के लिए यहाँ क्लिक करें" },
      { href: "/zakat-calculator", icon: "zakat", label: "अपने सोने पर कितनी ज़कात बनती है, अभी चेक करें" },
    ],
  },
};

function CtaIcon({ icon }: { icon: Cta["icon"] }) {
  const className = "h-5 w-5";
  if (icon === "city") return <Building2 className={className} aria-hidden />;
  if (icon === "silver") return <Gem className={className} aria-hidden />;
  if (icon === "zakat") return <Landmark className={className} aria-hidden />;
  if (icon === "calculator") return <Calculator className={className} aria-hidden />;
  return <Gem className={className} aria-hidden />;
}

export function HubNextSteps({ variant }: Props) {
  const cfg = CONFIG[variant];
  return (
    <section
      id="hub-next-steps"
      className="mx-auto max-w-5xl px-4 py-8"
      aria-label={`${variant} next-step navigation`}
    >
      <div className="grid gap-3">
        {cfg.ctas.map((c) => (
          <Link
            key={c.href}
            to={c.href}
            className="group flex items-center gap-3 rounded-xl border border-gold/40 bg-gradient-to-br from-card via-card to-gold/5 px-4 py-4 shadow-sm hover:border-primary hover:shadow-md transition-all"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
              <CtaIcon icon={c.icon} />
            </span>
            <span
              lang={cfg.lang}
              dir={cfg.dir}
              className="flex-1 min-w-0 text-sm md:text-base font-bold leading-snug text-foreground group-hover:text-primary"
            >
              {c.label}
            </span>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs md:text-sm font-bold text-primary-foreground group-hover:bg-primary/90 whitespace-nowrap">
              Click here →
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
